import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ReactionBar } from '../common/ReactionBar';
import { alpha, tokens } from '../../theme/tokens';
import { usePrefersReducedMotion } from '../../motion/transitions';
import { viewerReactionFromArticle } from '../../utils/articleReactions';
import { categoryLabel, displayTitle } from '../../utils/labels';
import { formatDate } from '../../utils/format';

function shouldClamp(summary) {
  const value = String(summary || '');
  return value.length > 100 || value.split(/\s+/).length > 20;
}

function stopCardClick(e) {
  e.stopPropagation();
}

export function ArticleCard({
  article,
  featured = false,
  index = 0,
  onReact,
  onShare,
  busy = false,
}) {
  const location = useLocation();
  const reduceMotion = usePrefersReducedMotion();
  const delayMs = reduceMotion ? 0 : Math.min(index, 8) * 55;
  const clamp = shouldClamp(article.summary);
  const myReaction = viewerReactionFromArticle(article);

  return (
    <Card
      sx={{
        bgcolor: featured ? undefined : 'background.paper',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative',
        borderRadius: { xs: 2, sm: 2.5 },
        '@keyframes prasaarCardIn': {
          from: {
            transform: reduceMotion ? 'none' : 'translateY(8px)',
          },
          to: { transform: 'none' },
        },
        animation: `prasaarCardIn ${reduceMotion ? 160 : 360}ms cubic-bezier(0.16, 1, 0.3, 1)`,
        animationDelay: `${delayMs}ms`,
        transition:
          'transform 280ms ease, box-shadow 320ms ease, border-color 280ms ease, background-color 280ms ease',
        ...(featured
          ? {
              borderColor: tokens.brandFill,
              borderWidth: 1.5,
              boxShadow: `0 12px 28px ${alpha(tokens.shellDeep, 0.28)}`,
            }
          : {
              boxShadow: `0 6px 18px ${alpha(tokens.shellDeep, 0.14)}`,
            }),
        '@media (hover: hover)': {
          '&:hover': {
            transform: reduceMotion ? 'none' : 'translateY(-3px)',
            borderColor: featured ? tokens.brandInk : tokens.borderHover,
            boxShadow: `0 14px 32px ${alpha(tokens.shellDeep, 0.32)}`,
          },
        },
        '&::before': featured
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: tokens.brandFill,
            }
          : undefined,
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/article/${article.id}`}
        state={{ from: `${location.pathname}${location.search}` }}
      >
        <CardContent
          sx={{
            p: featured
              ? { xs: 2, sm: 2.75 }
              : { xs: 1.75, sm: 2.25 },
            pb: { xs: 0.75, sm: 1 },
            pl: featured ? { xs: 2.5, sm: 3 } : undefined,
            '&:last-child': {
              pb: { xs: 0.5, sm: 0.75 },
            },
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ mb: 0.75, flexWrap: 'wrap' }}
          >
            {featured && (
              <Chip
                size="small"
                color="secondary"
                label="Featured"
                sx={{ fontWeight: 700, height: 28, fontSize: '0.75rem' }}
              />
            )}
            {(article.categories ?? []).slice(0, 2).map((c) => (
              <Chip
                key={c}
                size="small"
                label={categoryLabel(c)}
                variant="outlined"
                sx={{ height: 28, fontSize: '0.75rem' }}
              />
            ))}
          </Stack>
          <Typography
            variant={featured ? 'h5' : 'h6'}
            component="h2"
            sx={{
              mb: 0.5,
              lineHeight: 1.28,
              letterSpacing: '-0.02em',
              fontSize: featured
                ? { xs: '1.2rem', sm: '1.4rem', md: '1.55rem' }
                : { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
              overflowWrap: 'break-word',
            }}
          >
            {displayTitle(article.title)}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              mb: featured ? 0 : 0.5,
              lineHeight: 1.5,
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              overflowWrap: 'break-word',
              ...(clamp
                ? {
                    display: '-webkit-box',
                    WebkitLineClamp: { xs: 2, sm: featured ? 3 : 2 },
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }
                : null),
            }}
          >
            {article.summary}
            {clamp ? (
              <>
                {' '}
                <Link
                  component="span"
                  underline="hover"
                  sx={{
                    fontWeight: 700,
                    color: tokens.brandInk,
                  }}
                >
                  Open story
                </Link>
              </>
            ) : null}
          </Typography>
          {(article.publishedAt || article.sourceName || typeof article.commentCount === 'number') ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
            >
              {[
                formatDate(article.publishedAt),
                article.sourceName,
                typeof article.commentCount === 'number'
                  ? `${article.commentCount} comment${article.commentCount === 1 ? '' : 's'}`
                  : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          ) : null}
        </CardContent>
      </CardActionArea>
      <Box
        onClick={stopCardClick}
        onKeyDown={stopCardClick}
        sx={{
          px: featured
            ? { xs: 2, sm: 2.75 }
            : { xs: 1.75, sm: 2.25 },
          pl: featured ? { xs: 2.5, sm: 3 } : undefined,
          pb: featured
            ? { xs: 1.25, sm: 2 }
            : { xs: 1, sm: 1.5 },
        }}
      >
        <ReactionBar
          compact
          myReaction={myReaction}
          reactionCounts={article.reactions}
          shareCount={article.shareCount ?? 0}
          onReact={(emoji) => onReact?.(article, emoji)}
          onShare={() => onShare?.(article)}
          disabled={busy}
        />
      </Box>
    </Card>
  );
}
